import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '@/api/http';
import { AdminPageHeader } from '@/components/admin/admin-shell';

export const Route = createFileRoute('/admin/notas-fiscais')({ component: AdminNFs });

const money = (v: any) => Number(v ?? 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

type SessionRow = {
  id: string;
  scheduledAt: string;
  durationMin: number;
  priceCharged: string | null;
  client: { user: { name: string } };
};
type Cycle = {
  id: string;
  periodStart: string;
  periodEnd: string;
  nfDeadline: string;
  totalSessions: number;
  netAmount: string;
  therapist: { user: { name: string; email: string } };
  invoiceNF: {
    nfseNumber: string | null;
    nfsePdfUrl: string | null;
    submittedAt: string | null;
    extractedNumber: string | null;
    extractedAmount: string | null;
    extractedCnpj: string | null;
    validationMessages: string[];
  } | null;
  sessions: SessionRow[];
};

function AdminNFs() {
  const qc = useQueryClient();
  const [rejectFor, setRejectFor] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [expanded, setExpanded] = useState<string | null>(null);

  const { data = [], isLoading } = useQuery({
    queryKey: ['admin-invoices-pending'],
    queryFn: () => api.get('admin/invoices/pending').json<Cycle[]>(),
  });

  const invalidateAll = () => {
    qc.invalidateQueries({ queryKey: ['admin-invoices-pending'] });
    qc.invalidateQueries({ queryKey: ['admin-cycles'] });
    qc.invalidateQueries({ queryKey: ['admin-stats'] });
  };

  const approve = useMutation({
    mutationFn: (cycleId: string) => api.post(`billing/cycles/${cycleId}/approve`).json<any>(),
    onSuccess: invalidateAll,
  });

  const reject = useMutation({
    mutationFn: ({ cycleId, reason }: { cycleId: string; reason: string }) =>
      api.post(`billing/cycles/${cycleId}/reject`, { json: { reason } }).json<any>(),
    onSuccess: () => {
      invalidateAll();
      setRejectFor(null);
      setReason('');
    },
  });

  const today = new Date();

  return (
    <>
      <AdminPageHeader
        title="Notas fiscais — análise"
        subtitle={`${data.length} aguardando · aprovar libera o repasse no 1º`}
      />

      {isLoading ? (
        <div className="bg-white rounded-3xl p-12 animate-pulse h-32 border border-slate-100" />
      ) : data.length === 0 ? (
        <div className="bg-white rounded-3xl p-12 text-center border border-slate-100 shadow-sm">
          <p className="text-slate-500">Nenhuma NF aguardando análise.</p>
        </div>
      ) : (
        <ul className="space-y-3">
          {data.map((c) => {
            const deadline = new Date(c.nfDeadline);
            const overdue = deadline < today;
            const daysLeft = Math.ceil((deadline.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
            return (
              <li
                key={c.id}
                id={`cycle-${c.id}`}
                className="bg-white rounded-3xl p-5 border border-slate-100 shadow-sm scroll-mt-6"
              >
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-slate-800">{c.therapist.user.name}</p>
                    <p className="text-xs text-slate-500">{c.therapist.user.email}</p>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3 text-sm">
                      <Stat
                        label="Período"
                        value={`${new Date(c.periodStart).toLocaleDateString('pt-BR')} → ${new Date(c.periodEnd).toLocaleDateString('pt-BR')}`}
                      />
                      <Stat label="Sessões" value={String(c.totalSessions)} />
                      <Stat label="Valor da NF" value={money(c.netAmount)} />
                      <Stat
                        label="Prazo"
                        value={deadline.toLocaleDateString('pt-BR')}
                        sub={overdue ? 'Vencido' : daysLeft === 0 ? 'Hoje' : `Faltam ${daysLeft}d`}
                        tone={overdue ? 'red' : daysLeft <= 2 ? 'amber' : 'slate'}
                      />
                    </div>

                    {c.invoiceNF?.nfsePdfUrl && (
                      <a
                        href={c.invoiceNF.nfsePdfUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-block mt-3 text-xs text-teal-600 hover:underline"
                      >
                        📄 Abrir arquivo da NF →
                      </a>
                    )}
                    {c.invoiceNF?.nfseNumber && (
                      <div className="text-xs text-slate-500 mt-1">
                        Número da NFSe: <strong className="text-slate-700">{c.invoiceNF.nfseNumber}</strong>
                      </div>
                    )}

                    {/* Validação automática (NF-06b) — extração + comparação com terapeuta/ciclo */}
                    {c.invoiceNF?.validationMessages && c.invoiceNF.validationMessages.length > 0 && (
                      <ValidationBlock messages={c.invoiceNF.validationMessages} />
                    )}

                    <button
                      onClick={() => setExpanded(expanded === c.id ? null : c.id)}
                      className="text-xs text-slate-500 hover:text-slate-700 mt-3 inline-flex items-center gap-1"
                    >
                      {expanded === c.id ? '▾ Ocultar sessões' : `▸ Ver ${c.sessions.length} sessões cobertas`}
                    </button>
                  </div>

                  <span className="text-[10px] px-2 py-0.5 rounded border bg-indigo-50 text-indigo-700 border-indigo-200 shrink-0">
                    Em análise
                  </span>
                </div>

                {expanded === c.id && (
                  <div className="mt-4 pt-4 border-t border-slate-100">
                    <table className="w-full text-xs">
                      <thead className="text-slate-400 uppercase tracking-wide">
                        <tr>
                          <th className="text-left py-1.5 font-semibold">Data</th>
                          <th className="text-left py-1.5 font-semibold">Cliente</th>
                          <th className="text-right py-1.5 font-semibold">Min</th>
                          <th className="text-right py-1.5 font-semibold">Valor</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {c.sessions.map((s) => (
                          <tr key={s.id}>
                            <td className="py-1.5 text-slate-600 whitespace-nowrap">
                              {new Date(s.scheduledAt).toLocaleString('pt-BR', {
                                day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', timeZone: 'UTC',
                              })}
                            </td>
                            <td className="py-1.5 text-slate-700">{s.client.user.name}</td>
                            <td className="py-1.5 text-right text-slate-500 tabular-nums">{s.durationMin}</td>
                            <td className="py-1.5 text-right text-slate-700 tabular-nums">{money(s.priceCharged)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {rejectFor === c.id ? (
                  <div className="mt-4 pt-4 border-t border-slate-100 space-y-2">
                    <textarea
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      placeholder="Motivo da rejeição (será enviado ao profissional)..."
                      rows={3}
                      className="w-full bg-white border border-slate-200 rounded-xl p-3 text-sm focus:outline-none focus:ring-2 focus:ring-coral-300"
                    />
                    <div className="flex gap-2">
                      <button
                        onClick={() => reject.mutate({ cycleId: c.id, reason })}
                        disabled={!reason.trim() || reject.isPending}
                        className="bg-coral-500 hover:bg-coral-600 text-white px-4 py-2 rounded-full text-sm font-medium transition disabled:opacity-50"
                      >
                        Confirmar rejeição
                      </button>
                      <button
                        onClick={() => { setRejectFor(null); setReason(''); }}
                        className="text-slate-500 hover:text-slate-700 text-sm"
                      >
                        Cancelar
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex gap-2 mt-4 pt-4 border-t border-slate-100">
                    <button
                      onClick={() => approve.mutate(c.id)}
                      disabled={approve.isPending}
                      className="bg-teal-500 hover:bg-teal-600 text-white px-4 py-2 rounded-full text-sm font-medium transition disabled:opacity-50"
                    >
                      ✓ Aprovar NF
                    </button>
                    <button
                      onClick={() => setRejectFor(c.id)}
                      disabled={approve.isPending}
                      className="bg-red-50 hover:bg-red-100 text-red-700 border border-red-200 px-4 py-2 rounded-full text-sm font-medium transition"
                    >
                      Rejeitar
                    </button>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </>
  );
}

function ValidationBlock({ messages }: { messages: string[] }) {
  const hasError = messages.some((m) => m.startsWith('✗'));
  const hasWarn = messages.some((m) => m.startsWith('⚠'));
  const tone = hasError ? 'red' : hasWarn ? 'amber' : 'teal';
  const wrap =
    tone === 'red' ? 'border-red-200 bg-red-50' :
    tone === 'amber' ? 'border-amber-200 bg-amber-50' :
    'border-teal-200 bg-teal-50';
  return (
    <div className={`mt-3 rounded-md border px-3 py-2 ${wrap}`}>
      <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-600 mb-1.5">
        Validação automática do PDF
      </p>
      <ul className="space-y-1">
        {messages.map((m, i) => {
          const tone =
            m.startsWith('✗') ? 'text-red-700' :
            m.startsWith('⚠') ? 'text-amber-800' :
            'text-teal-800';
          return (
            <li key={i} className={`text-xs ${tone}`}>{m}</li>
          );
        })}
      </ul>
    </div>
  );
}

function Stat({
  label, value, sub, tone = 'slate',
}: { label: string; value: string; sub?: string; tone?: 'slate' | 'amber' | 'red' }) {
  const subColor = tone === 'red' ? 'text-red-600' : tone === 'amber' ? 'text-amber-600' : 'text-slate-400';
  return (
    <div>
      <div className="text-[10px] text-slate-500 uppercase tracking-wide font-semibold">{label}</div>
      <div className="font-semibold text-slate-800 text-sm mt-0.5">{value}</div>
      {sub && <div className={`text-[10px] mt-0.5 font-semibold ${subColor}`}>{sub}</div>}
    </div>
  );
}
